import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { Mail, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const ForgotPassword = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4 sm:p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <Link to="/login" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-xl tracking-tight text-[#111111]">FUTURA</span>
          </Link>
          <LanguageSelector variant="light" />
        </div>

        <div className="animate-fade-in">
          {!sent ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <Link to="/login" className="text-[#6B7280] hover:text-[#111111]"><ArrowLeft className="w-5 h-5" /></Link>
                <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#111111] tracking-tight" data-testid="forgot-title">{t("forgot_title")}</h1>
              </div>
              <p className="text-[#6B7280] mb-6 text-sm">{t("forgot_subtitle_email")}</p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-[#EF4444]/10 text-[#EF4444] rounded-xl" data-testid="forgot-error"><AlertCircle className="w-4 h-4 flex-shrink-0" /><p className="text-sm">{error}</p></div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#111111] font-medium">{t("forgot_email_label")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl" required autoComplete="email" data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl" data-testid="forgot-submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("forgot_send_link")}<ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
              <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-3" data-testid="forgot-sent-title">{t("forgot_sent_title")}</h1>
              <p className="text-[#6B7280] mb-2">{t("forgot_sent_text")}</p>
              <p className="text-[#111111] font-medium mb-8">{email}</p>
              <p className="text-[#9CA3AF] text-sm">{t("forgot_sent_check_spam")}</p>
              <Link to="/login" className="inline-block mt-6 text-[#002FA7] hover:underline font-medium text-sm" data-testid="forgot-back-login">{t("verify_back_login")}</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
